import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, Mail, MessageSquare, User } from 'lucide-react';

export const contactForm2: Playbook = {
  meta: {
    id: 'contact-form-2',
    slug: 'contact-form-2',
    categoryId: 'capture-leads',
    name: 'The Contact Form 2.0',
    tagline: 'Make your contact page interactive.',
    description: 'Replace your static contact form with an interactive conversation to gather richer information and provide instant answers.',
    strategy: 'Replace your static contact form with an interactive conversation to get richer information and provide instant answers.',
    benefits: [
      'Higher form completion rates',
      'Richer lead qualification',
      'Instant responses to questions',
      'Better user experience'
    ],
    targetAudience: ['B2B services', 'Professional services', 'SaaS companies'],
    estimatedSetupTime: '15 minutes',
    difficulty: 'beginner',
    tags: ['contact-form', 'lead-qualification', 'interactive', 'conversion'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Contact Form 2.0 Playbook - Interactive Genie Contact Forms',
    metaDescription: 'Transform static contact forms into engaging Genie conversations. Increase completion rates and gather better lead information.',
    keywords: ['interactive contact form', 'Genie contact form', 'lead capture', 'form completion', 'contact page']
  },
  demoConfig: {
    triggerType: 'button',
    message: "Hello! I'm here to help. Whether you have a question, need support, or want to learn more about our services, I'm all ears. How can I assist you today?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Hello! How can I help you today?" },
    { speaker: 'user', message: "I'd like to learn more about your enterprise features" },
    { speaker: 'agent', message: "Great! Our enterprise plan includes SSO, custom integrations, and dedicated support." },
    { speaker: 'user', message: "Can I get a demo?" },
    { speaker: 'agent', message: "Absolutely! I can schedule one for you. What's your email?" }
  ],
  templateConfig: {
    firstMessage: "Hello! I'm here to help with any questions you have. How can I assist you today?",
    systemPrompt: "You are a helpful contact Genie. Gather relevant information about the visitor's inquiry, provide immediate help when possible, and collect contact details for follow-up when needed.",
    tools: ['email_capture', 'ticket_creation']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Visitor has inquiry',
      description: 'User visits contact page with question, request, or interest in services'
    },
    {
      type: 'genie',
      icon: MessageSquare,
      genieIcon: 'genie-lamp',
      label: 'Starts conversation',
      description: 'Genie greets visitor and opens natural dialogue instead of showing static form'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Qualifies inquiry type',
      description: 'Understands whether user needs support, sales info, or has specific questions'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'database',
      label: 'Gathers rich context',
      description: 'Collects detailed information about needs, timeline, and requirements naturally'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures contact details',
      description: 'Obtains email, phone, company info when appropriate in conversation flow'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Qualified lead created',
      description: 'Better information gathered with higher completion rate than static forms'
    }
  ],
  metrics: {
    conversionRate: '47%',
    responseTime: '<1 second',
    satisfactionScore: '4.5/5',
    costSavings: '$15k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'Legal Solutions Pro',
      authorName: 'Jennifer Martinez',
      authorTitle: 'Marketing Director',
      quote: 'Our contact form completion rate increased by 65% and lead quality improved significantly with the conversational approach.',
      metrics: '65% increase in form completion'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Contact Form Conversation Templates',
      description: 'Pre-built conversation flows for different inquiry types',
      url: '/templates/contact-conversations'
    },
    {
      type: 'guide',
      title: 'Lead Qualification Best Practices',
      description: 'How to gather better lead information through conversation',
      url: '/docs/lead-qualification'
    }
  ]
};